import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Calculator, RotateCcw } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';

const initialValues = { lineCsa: '', cpcCsa: '', lineR1: '', cpcR2: '' };

/**
 * RFC Line to CPC Ratio Calculator
 * Formula: r2 = r1 × (Line CSA / CPC CSA)
 * Resistance is inversely proportional to CSA, so larger Line CSA → smaller r1; smaller CPC CSA → larger r2
 */
function InputField({ label, placeholder, value, onChange, readOnly = false }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
      <label className="flex shrink-0 items-center px-3 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium min-w-[5rem]">
        {label}
      </label>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className={`flex-1 min-w-0 px-3 py-2.5 text-sm border-0 focus:outline-none focus:ring-0 bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 ${
          readOnly ? 'cursor-default bg-gray-100 dark:bg-gray-600/50' : ''
        }`}
        aria-label={label}
      />
    </div>
  );
}

export default function RFCLineCPCRatioCalc() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [values, setValues] = useState(initialValues);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const updateValue = (field, val) => {
    setValues((p) => ({ ...p, [field]: val, cpcR2: '' }));
  };

  const handleCalculate = () => {
    const lineCsa = parseFloat(values.lineCsa);
    const cpcCsa = parseFloat(values.cpcCsa);
    const lineR1 = parseFloat(values.lineR1);

    if (!lineCsa || !cpcCsa || !lineR1 || lineCsa <= 0 || cpcCsa <= 0 || lineR1 <= 0) return;

    // r2 = r1 × (Line CSA / CPC CSA)
    const cpcR2 = lineR1 * (lineCsa / cpcCsa);
    setValues((p) => ({ ...p, cpcR2: cpcR2.toFixed(4) }));
  };

  const handleReset = () => {
    setValues({ ...initialValues });
  };

  const lineCsa = parseFloat(values.lineCsa);
  const cpcCsa = parseFloat(values.cpcCsa);
  const lineR1 = parseFloat(values.lineR1);
  const canCalculate =
    lineCsa > 0 && cpcCsa > 0 && lineR1 > 0 &&
    !isNaN(lineCsa) && !isNaN(cpcCsa) && !isNaN(lineR1);

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">Line to Cpc Ratio Calculator</h1>
        <Link
          to={isMobile ? '/tenant/mobile' : '/tenant/dashboard'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4">
        Enter a ring circuit line &amp; cpc conductor csa and r1 (Line) end to end value to calculate
        the expected r2 (cpc) end to end value
      </p>

      <div className={`space-y-4 ${isMobile ? 'pb-8' : ''}`}>
        <InputField
          label="Line csa"
          placeholder="mm²"
          value={values.lineCsa}
          onChange={(val) => updateValue('lineCsa', val)}
        />
        <InputField
          label="Cpc csa"
          placeholder="mm²"
          value={values.cpcCsa}
          onChange={(val) => updateValue('cpcCsa', val)}
        />
        <InputField
          label="Line r1"
          placeholder="Ω"
          value={values.lineR1}
          onChange={(val) => updateValue('lineR1', val)}
        />
        <InputField
          label="Cpc r2"
          placeholder="Result Ω"
          value={values.cpcR2}
          onChange={() => {}}
          readOnly
        />

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
          <Button
            onClick={handleReset}
            variant="destructive"
            className="flex-1 py-3 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>

        {/* Formula reference */}
        <div className="mt-6 space-y-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Formula: r2 = r1 × (Line CSA / CPC CSA)
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Resistance is inversely proportional to conductor CSA. For 2.5/1.5mm² T&amp;E,
            r2 ≈ 1.67 × r1.
          </p>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Line to CPC Ratio Calculator
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Ring final circuit — calculate expected r2 (CPC) end-to-end from line and CPC CSA and r1
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
